import { UploadOutlined } from "@ant-design/icons";
import { Input, Tooltip, Upload } from "@svg/antd";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchDropdownPanel from "../SearchDropdownPanel";
import type { TopicEntry } from "../SearchDropdownPanel/components/EmptyState";

type InputProps = React.ComponentProps<typeof Input>;
type UploadComponentProps = React.ComponentProps<typeof Upload>;
type SearchSource = "enter" | "prefix";

interface SearchUploadProps extends Omit<UploadComponentProps, "children"> {
  visible?: boolean;
  icon?: React.ReactNode;
  tooltip?: React.ReactNode;
}

export interface SearchProps
  extends Omit<
    InputProps,
    | "value"
    | "defaultValue"
    | "prefix"
    | "suffix"
    | "size"
    | "onChange"
    | "onFocus"
    | "onBlur"
    | "onPressEnter"
  > {
  value?: string;
  defaultValue?: string;
  size?: "large" | "normal";
  inputSize?: InputProps["size"];
  dropdownSize?: "large" | "normal";
  visible?: boolean;
  defaultVisible?: boolean;
  blurHideDelay?: number;
  closeOnBlur?: boolean;
  closeOnSearch?: boolean;
  closeOnSelect?: boolean;
  syncValueOnSelectTypes?: string[];
  prefix?: React.ReactNode;
  onPrefixClick?: (value: string, event: React.MouseEvent<HTMLElement>) => void;
  suffix?: React.ReactNode;
  showSuffix?: boolean;
  uploadProps?: SearchUploadProps;
  onChange?: (
    value: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onSearch?: (value: string, source: SearchSource) => void;
  onPressEnter?: (
    value: string,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (value: string, event: React.FocusEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  onVisibleChange?: (visible: boolean) => void;
  onSelect?: (searchValue: string, select: any, type: string) => void;
  searchHistory?: string[];
  hotSearch?: string[];
  topicEntries?: TopicEntry[];
  searchCategorie?: Array<{
    key: number;
    value: string;
    hightLight: string;
  }>;
  recommendation?: string[];
  categoriesRecommend?: Array<{ key: number; value: string[] }>;
  onClearHistory?: () => void;
}

const DEFAULT_SYNC_TYPES = [
  "searchHistory",
  "hotSearch",
  "recommendation",
  "searchCategorie",
];

const DefaultSearchIcon = (
  <svg
    viewBox="0 0 1024 1024"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: 16, height: 16, display: "block" }}
  >
    <path
      d="M480 115.2a364.8 364.8 0 0 1 283.6992 594.176l141.0048 141.056a38.4 38.4 0 0 1-48.9472 58.7776l-5.3248-4.5056-141.056-141.0048A364.8 364.8 0 1 1 480 115.2z m0 76.8a288 288 0 1 0 0 576 288 288 0 0 0 0-576z"
      fill="#747B81"
    />
  </svg>
);

const getSelectValue = (searchValue: string, select: any, type: string) => {
  switch (type) {
    case "searchHistory":
    case "hotSearch":
    case "searchCategorie":
      return searchValue;
    case "recommendation":
      return typeof select === "string" ? select : searchValue;
    default:
      return null;
  }
};

const Search = forwardRef<any, SearchProps>((props, ref) => {
  const {
    value,
    defaultValue = "",
    size = "normal",
    inputSize,
    dropdownSize,
    visible,
    defaultVisible = false,
    blurHideDelay = 100,
    closeOnBlur = true,
    closeOnSearch = true,
    closeOnSelect = true,
    syncValueOnSelectTypes = DEFAULT_SYNC_TYPES,
    prefix,
    onPrefixClick,
    suffix,
    showSuffix = true,
    uploadProps,
    onChange,
    onSearch,
    onPressEnter,
    onFocus,
    onBlur,
    onClick,
    onClear,
    onVisibleChange,
    onSelect,
    searchHistory,
    hotSearch,
    topicEntries,
    searchCategorie,
    recommendation,
    categoriesRecommend,
    onClearHistory,
    style: inputStyle,
    ...inputProps
  } = props;

  const [innerValue, setInnerValue] = useState(defaultValue);
  const [innerVisible, setInnerVisible] = useState(defaultVisible);
  const blurTimerRef = useRef<NodeJS.Timeout | null>(null);
  const uploadInteractionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const suppressOpenAfterUploadRef = useRef(false);
  const isMouseInPanelRef = useRef(false);
  const isInputFocusedRef = useRef(false);
  const isControlledValue = value !== undefined;
  const isControlledVisible = visible !== undefined;
  const mergedValue = isControlledValue ? value : innerValue;
  const mergedVisible = isControlledVisible ? visible : innerVisible;
  const mergedDropdownSize = dropdownSize ?? size;
  const mergedInputSize = inputSize ?? (size === "large" ? "large" : "middle");

  const updateValue = useCallback(
    (nextValue: string) => {
      if (!isControlledValue) {
        setInnerValue(nextValue);
      }
    },
    [isControlledValue]
  );

  const updateVisible = useCallback(
    (nextVisible: boolean) => {
      if (!isControlledVisible) {
        setInnerVisible(nextVisible);
      }
      onVisibleChange?.(nextVisible);
    },
    [isControlledVisible, onVisibleChange]
  );

  const clearBlurTimer = useCallback(() => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  }, []);

  const clearUploadInteractionTimer = useCallback(() => {
    if (uploadInteractionTimerRef.current) {
      clearTimeout(uploadInteractionTimerRef.current);
      uploadInteractionTimerRef.current = null;
    }
  }, []);

  const finishUploadInteraction = useCallback(() => {
    clearUploadInteractionTimer();
    uploadInteractionTimerRef.current = setTimeout(() => {
      suppressOpenAfterUploadRef.current = false;
      uploadInteractionTimerRef.current = null;
    }, 150);
  }, [clearUploadInteractionTimer]);

  const startUploadInteraction = useCallback(() => {
    suppressOpenAfterUploadRef.current = true;
    clearUploadInteractionTimer();

    if (typeof window !== "undefined") {
      window.addEventListener("focus", finishUploadInteraction, {
        once: true,
      });
    }
  }, [clearUploadInteractionTimer, finishUploadInteraction]);

  useEffect(() => {
    return () => {
      clearBlurTimer();
      clearUploadInteractionTimer();
    };
  }, [clearBlurTimer, clearUploadInteractionTimer]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      const hadValue = !!mergedValue;

      updateValue(nextValue);
      onChange?.(nextValue, event);
      updateVisible(true);

      if (!nextValue && hadValue) {
        onClear?.();
      }
    },
    [mergedValue, onChange, onClear, updateValue, updateVisible]
  );

  const triggerSearch = useCallback(
    (source: SearchSource) => {
      onSearch?.(mergedValue || "", source);
      if (closeOnSearch) {
        updateVisible(false);
      }
    },
    [closeOnSearch, mergedValue, onSearch, updateVisible]
  );

  const handlePressEnter = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      clearBlurTimer();
      onPressEnter?.(mergedValue || "", event);
      triggerSearch("enter");
    },
    [clearBlurTimer, mergedValue, onPressEnter, triggerSearch]
  );

  const handlePrefixMouseDown = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      clearBlurTimer();
    },
    [clearBlurTimer]
  );

  const handlePrefixClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (onPrefixClick) {
        onPrefixClick(mergedValue || "", event);
        if (closeOnSearch) {
          updateVisible(false);
        }
        return;
      }

      if (onSearch) {
        triggerSearch("prefix");
      }
    },
    [
      closeOnSearch,
      mergedValue,
      onPrefixClick,
      onSearch,
      triggerSearch,
      updateVisible,
    ]
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      clearBlurTimer();
      isInputFocusedRef.current = true;
      if (suppressOpenAfterUploadRef.current) {
        return;
      }
      updateVisible(true);
      onFocus?.(event);
    },
    [clearBlurTimer, onFocus, updateVisible]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      clearBlurTimer();
      if (suppressOpenAfterUploadRef.current) {
        return;
      }
      updateVisible(true);
      onClick?.(event);
    },
    [clearBlurTimer, onClick, updateVisible]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      isInputFocusedRef.current = false;
      onBlur?.(mergedValue || "", event);
      clearBlurTimer();
      if (!closeOnBlur) {
        return;
      }
      blurTimerRef.current = setTimeout(() => {
        if (!isMouseInPanelRef.current) {
          updateVisible(false);
        }
        blurTimerRef.current = null;
      }, blurHideDelay);
    },
    [
      blurHideDelay,
      clearBlurTimer,
      closeOnBlur,
      mergedValue,
      onBlur,
      updateVisible,
    ]
  );

  const handlePanelMouseEnter = useCallback(() => {
    isMouseInPanelRef.current = true;
    clearBlurTimer();
  }, [clearBlurTimer]);

  const handlePanelMouseLeave = useCallback(() => {
    isMouseInPanelRef.current = false;
    if (closeOnBlur && !isInputFocusedRef.current) {
      updateVisible(false);
    }
  }, [closeOnBlur, updateVisible]);

  const handleSelect = useCallback(
    (searchValue: string, select: any, type: string) => {
      clearBlurTimer();

      if (syncValueOnSelectTypes.includes(type)) {
        const nextValue = getSelectValue(searchValue, select, type);
        if (typeof nextValue === "string") {
          updateValue(nextValue);
        }
      }

      onSelect?.(searchValue, select, type);

      if (closeOnSelect) {
        updateVisible(false);
      }
    },
    [
      clearBlurTimer,
      closeOnSelect,
      onSelect,
      syncValueOnSelectTypes,
      updateValue,
      updateVisible,
    ]
  );

  const renderedPrefix = useMemo(() => {
    const prefixNode = prefix === undefined ? DefaultSearchIcon : prefix;
    const isClickable = !!onPrefixClick || !!onSearch;

    if (!prefixNode) {
      return null;
    }

    if (!isClickable) {
      return prefixNode;
    }

    return (
      <span
        role="button"
        tabIndex={0}
        style={{ cursor: "pointer", display: "inline-flex" }}
        onMouseDown={handlePrefixMouseDown}
        onClick={handlePrefixClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handlePrefixClick(
              event as unknown as React.MouseEvent<HTMLElement>
            );
          }
        }}
      >
        {prefixNode}
      </span>
    );
  }, [
    handlePrefixClick,
    handlePrefixMouseDown,
    onPrefixClick,
    onSearch,
    prefix,
  ]);

  const renderedUploadSuffix = useMemo(() => {
    if (!uploadProps?.visible) {
      return null;
    }

    const {
      visible: _visible,
      icon,
      tooltip,
      beforeUpload,
      onChange,
      showUploadList = false,
      ...restUploadProps
    } = uploadProps;

    const shieldUploadTrigger = (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      startUploadInteraction();
      clearBlurTimer();
      updateVisible(false);
    };

    const mergedBeforeUpload = async (...args: any[]) => {
      clearBlurTimer();
      updateVisible(false);
      if (!beforeUpload) {
        finishUploadInteraction();
        return true;
      }
      const result = await beforeUpload(...args);
      finishUploadInteraction();
      return result;
    };

    const mergedUploadChange = (...args: any[]) => {
      clearBlurTimer();
      updateVisible(false);
      onChange?.(...args);
      finishUploadInteraction();
    };

    const uploadNode = (
      <span
        style={{ display: "inline-flex", alignItems: "center" }}
        onMouseDown={shieldUploadTrigger}
        onClick={(event) => {
          event.stopPropagation();
          clearBlurTimer();
          updateVisible(false);
        }}
      >
        <Upload
          showUploadList={showUploadList}
          beforeUpload={mergedBeforeUpload}
          onChange={mergedUploadChange}
          {...restUploadProps}
        >
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {icon || <UploadOutlined />}
          </span>
        </Upload>
      </span>
    );

    if (!tooltip) {
      return uploadNode;
    }

    return <Tooltip title={tooltip}>{uploadNode}</Tooltip>;
  }, [
    clearBlurTimer,
    finishUploadInteraction,
    startUploadInteraction,
    updateVisible,
    uploadProps,
  ]);

  const renderedSuffix = useMemo(() => {
    if (!showSuffix) {
      return null;
    }

    if (!suffix && !renderedUploadSuffix) {
      return null;
    }

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {suffix}
        {renderedUploadSuffix}
      </span>
    );
  }, [renderedUploadSuffix, showSuffix, suffix]);

  return (
    <div
      onMouseEnter={handlePanelMouseEnter}
      onMouseLeave={handlePanelMouseLeave}
      style={{ width: "100%" }}
    >
      <SearchDropdownPanel
        visible={mergedVisible}
        onVisibleChange={updateVisible}
        searchValue={mergedValue || ""}
        onSelect={handleSelect}
        searchHistory={searchHistory}
        hotSearch={hotSearch}
        topicEntries={topicEntries}
        searchCategorie={searchCategorie}
        recommendation={recommendation}
        categoriesRecommend={categoriesRecommend}
        onClearHistory={onClearHistory}
        size={mergedDropdownSize}
      >
        <Input
          {...inputProps}
          ref={ref}
          style={{
            ...inputStyle,
            width: "100%",
          }}
          value={mergedValue}
          defaultValue={isControlledValue ? undefined : defaultValue}
          size={mergedInputSize}
          prefix={renderedPrefix}
          suffix={renderedSuffix}
          onChange={handleInputChange}
          onPressEnter={handlePressEnter}
          onFocus={handleFocus}
          onClick={handleClick}
          onBlur={handleBlur}
        />
      </SearchDropdownPanel>
    </div>
  );
});

Search.displayName = "Search";

export default Search;
